// ContactFormSection.js
import React, { useState } from "react";
import { Phone, Clock, MapPin, Mail } from "lucide-react";

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    interest: "",
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission with 3-second delay
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        interest: "",
        consent: false,
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    }, 3000);
  };

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Contact Form */}
        <div className="max-w-3xl mx-auto mb-20">
          <div className="text-center mb-10">
            <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Contact Us
            </p>
            <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl lg:text-5xl">
              Send Us a Message or Schedule a Callback
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="firstName"
                placeholder="First Name*"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-bnBorder rounded-lg text-bnInk placeholder:text-bnBody focus:outline-none focus:border-bnAccent transition-colors"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name*"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-bnBorder rounded-lg text-bnInk placeholder:text-bnBody focus:outline-none focus:border-bnAccent transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="email"
                name="email"
                placeholder="Email*"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-bnBorder rounded-lg text-bnInk placeholder:text-bnBody focus:outline-none focus:border-bnAccent transition-colors"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone*"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-bnBorder rounded-lg text-bnInk placeholder:text-bnBody focus:outline-none focus:border-bnAccent transition-colors"
              />
            </div>

            <select
              name="interest"
              value={formData.interest}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-bnBorder rounded-lg text-bnBody focus:outline-none focus:border-bnAccent transition-colors"
            >
              <option value="">I'm interested in...</option>
              <option value="alzheimers-dementia">
                Alzheimer's and Dementia Care
              </option>
              <option value="companionship">Companionship Care</option>
              <option value="hospice">Hospice Care</option>
              <option value="personal">Personal Care</option>
              <option value="post-operative">Post-Operative Care</option>
              <option value="respite">Respite Care</option>
              <option value="specialty">Specialty Care</option>
              <option value="spinal-cord">Spinal Cord Injury Care</option>
              <option value="general">General Inquiry</option>
            </select>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="consent"
                id="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="mt-1 w-4 h-4 text-bnAccent border-bnBorder rounded focus:ring-bnAccent"
              />
              <label
                htmlFor="consent"
                className="text-sm text-bnBody leading-relaxed"
              >
                By providing a telephone number and submitting this form, you
                are consenting to be contacted by SMS text message. Message and
                data rates may apply. Message frequency may vary.{" "}
                <a
                  href="/privacy-policy"
                  className="text-bnAccent hover:text-bnAccentDark transition-colors"
                >
                  Privacy Policy
                </a>
                . Reply Help for more information. You can reply STOP to opt out
                of further messaging.
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex items-center justify-center bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm py-4 rounded-full transition-colors disabled:bg-bnBody disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>

            {isSuccess && (
              <div className="mt-4 p-4 bg-bnCream border border-bnBorder rounded-lg">
                <p className="text-bnInk font-medium text-center">
                  Thank you for your message. We have received your enquiry and
                  will contact you shortly.
                </p>
              </div>
            )}
          </form>
        </div>

        {/* Get In Touch Section */}
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Get in Touch
            </p>
            <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl lg:text-5xl">
              B & N Enterprise
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {/* Phone */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border border-bnAccent rounded-full flex items-center justify-center mb-4 text-bnAccent">
                <Phone className="w-7 h-7" />
              </div>
              <h3 className="font-display font-normal text-bnInk text-xl mb-2">
                Phone
              </h3>
              <a
                href="tel:+61480804640"
                className="text-bnBody hover:text-bnAccent transition-colors"
              >
                +61 480 804 640
              </a>
            </div>

            {/* Email */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border border-bnAccent rounded-full flex items-center justify-center mb-4 text-bnAccent">
                <Mail className="w-7 h-7" />
              </div>
              <h3 className="font-display font-normal text-bnInk text-xl mb-2">
                Email
              </h3>
              <a
                href="mailto:info@bnenterprise.care"
                className="text-bnBody hover:text-bnAccent transition-colors break-all"
              >
                info@bnenterprise.care
              </a>
            </div>

            {/* Location */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border border-bnAccent rounded-full flex items-center justify-center mb-4 text-bnAccent">
                <MapPin className="w-7 h-7" />
              </div>
              <h3 className="font-display font-normal text-bnInk text-xl mb-2">
                Address
              </h3>
              <p className="text-bnBody text-sm leading-relaxed">
                23 Mahony Ct
                <br />
                Weston ACT 2611
                <br />
                Australia
              </p>
            </div>

            {/* Office Hours */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border border-bnAccent rounded-full flex items-center justify-center mb-4 text-bnAccent">
                <Clock className="w-7 h-7" />
              </div>
              <h3 className="font-display font-normal text-bnInk text-xl mb-2">
                Office Hours
              </h3>
              <p className="text-bnBody text-sm leading-relaxed">
                8:30 a.m. to 5:00 p.m.
                <br />
                Monday to Friday
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
